import React, { useState } from "react";
import { Scale, Send, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const CONTACT_EMAIL = "savageboyuncensored@gmail.com";

export default function DMCA() {
  const [form, setForm] = useState({
    name: "", email: "", company: "", copyrighted_work: "", infringing_url: "", statement: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const canSubmit =
    form.name.trim() && form.email.trim() && form.copyrighted_work.trim() &&
    form.infringing_url.trim() && form.statement.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) { toast.error("Please fill in all required fields"); return; }
    setBusy(true);
    try {
      await api.post("/dmca/submit", form);
      setSent(true);
      toast.success("Notice submitted");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const mailto = () => {
    const body = encodeURIComponent(
      `NAME: ${form.name}\nEMAIL: ${form.email}\nCOMPANY: ${form.company}\n\n` +
      `COPYRIGHTED WORK:\n${form.copyrighted_work}\n\n` +
      `INFRINGING URL(S):\n${form.infringing_url}\n\n` +
      `STATEMENT:\n${form.statement}\n\n` +
      `I have a good faith belief that use of the material described above is not authorized ` +
      `by the copyright owner, its agent, or the law. The information in this notice is accurate, ` +
      `and under penalty of perjury, I am the copyright owner or authorized to act on the ` +
      `copyright owner's behalf.\n\nSigned electronically,\n${form.name}`
    );
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("DMCA Takedown Notice — savageboy.net")}&body=${body}`;
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="dmca-page">
      <div className="flex items-center gap-3 mb-3">
        <span className="w-10 h-10 rounded-xl bg-[#E50914]/15 border border-[#E50914]/40 flex items-center justify-center">
          <Scale className="w-5 h-5 text-[#E50914]" />
        </span>
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#E50914] font-bold">Legal</span>
      </div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-3">
        DMCA Policy & Takedown Notice
      </h1>
      <p className="text-zinc-400 mb-10 max-w-2xl">
        SavageBoy respects the intellectual property rights of others. If you believe your
        copyrighted work has been used on this website without authorization, submit the notice
        below and we will act promptly.
      </p>

      <div className="glass rounded-2xl p-6 sm:p-8 space-y-6 text-zinc-300 leading-relaxed mb-10">
        <Section title="Our Commitment">
          <p>
            savageboy.net (the "Website") complies with the Digital Millennium Copyright Act, 17
            U.S.C. § 512 ("DMCA"). It is our policy to respond expeditiously to valid takedown
            notices and to terminate the accounts of repeat infringers.
          </p>
        </Section>

        <Section title="Filing a Notice of Infringement">
          <p>To be effective, a notice must include all of the following:</p>
          <ol className="list-decimal pl-5 space-y-1.5 text-sm text-zinc-400">
            <li>A physical or electronic signature of the copyright owner or authorized agent.</li>
            <li>Identification of the copyrighted work claimed to have been infringed.</li>
            <li>Identification of the material that is claimed to be infringing, with sufficient information (e.g. URL) to permit us to locate it.</li>
            <li>Your name, mailing address, telephone number, and email address.</li>
            <li>A statement that you have a good-faith belief that the disputed use is not authorized by the copyright owner, its agent, or the law.</li>
            <li>A statement, under penalty of perjury, that the information in the notice is accurate and that you are the copyright owner or are authorized to act on the copyright owner's behalf.</li>
          </ol>
        </Section>

        <Section title="Where to Send Notices">
          <div className="rounded-xl bg-white/5 border border-white/10 p-4 text-sm">
            <div><span className="text-zinc-500">Designated Agent:</span> SavageBoy</div>
            <div><span className="text-zinc-500">Email:</span>{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#E50914] hover:underline">{CONTACT_EMAIL}</a>
            </div>
            <div className="text-xs text-zinc-500 mt-2">
              Please use the subject line "DMCA Takedown Notice — savageboy.net". You can also use
              the form below and it will be logged into our system and forwarded to the same email.
            </div>
          </div>
        </Section>

        <Section title="Counter-Notification">
          <p>
            If you believe content of yours was removed in error, you may submit a counter-notice
            under 17 U.S.C. § 512(g) to the same address. Include your signature, contact info,
            identification of the removed material, and a statement under penalty of perjury that
            you believe the material was removed as a result of mistake or misidentification.
          </p>
        </Section>

        <Section title="Repeat Infringers">
          <p>
            Accounts of users who are determined to be repeat infringers may be suspended or
            terminated in accordance with the DMCA and our own community rules.
          </p>
        </Section>
      </div>

      {sent ? (
        <div className="glass-strong rounded-2xl p-8 text-center" data-testid="dmca-sent">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#32D74B]/20 border border-[#32D74B]/40 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-8 h-8 text-[#32D74B]" />
          </div>
          <h2 className="font-display text-2xl font-black">Notice received</h2>
          <p className="text-zinc-400 text-sm mt-2 max-w-lg mx-auto">
            Your DMCA notice was recorded. We'll respond as quickly as possible using the email
            address you provided. A copy has also been forwarded internally to{" "}
            <span className="text-white">{CONTACT_EMAIL}</span>.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="glass-strong rounded-2xl p-6 sm:p-8 space-y-4" data-testid="dmca-form">
          <h2 className="font-display font-bold text-2xl mb-2">DMCA Takedown Form</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <F label="Full name *" testid="dmca-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <F label="Email *" type="email" testid="dmca-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
          </div>
          <F label="Company / rights holder (optional)" testid="dmca-company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} />
          <Textarea label="Describe the copyrighted work *" testid="dmca-work" rows={3} value={form.copyrighted_work} onChange={(v) => setForm({ ...form, copyrighted_work: v })} placeholder="Title, description, and if possible a URL to the original work" />
          <Textarea label="URL(s) on savageboy.net where the infringement appears *" testid="dmca-urls" rows={3} value={form.infringing_url} onChange={(v) => setForm({ ...form, infringing_url: v })} placeholder="https://savageboy.net/videos/..." />
          <Textarea
            label="Statement under penalty of perjury *"
            testid="dmca-statement"
            rows={4}
            value={form.statement}
            onChange={(v) => setForm({ ...form, statement: v })}
            placeholder="I have a good-faith belief that the use is not authorized and, under penalty of perjury, that I am the copyright owner or authorized to act on their behalf."
          />
          <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
            <button
              data-testid="dmca-submit"
              type="submit"
              disabled={!canSubmit || busy}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#E50914] hover:bg-[#F6121D] text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" /> {busy ? "Submitting..." : "Submit notice"}
            </button>
            <button type="button" onClick={mailto} className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-sm">
              <Mail className="w-4 h-4" /> Or send via email client
            </button>
            <p className="text-xs text-zinc-500">By submitting, you agree the info above is accurate.</p>
          </div>
        </form>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display font-bold text-xl text-white mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function F({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 block mb-1.5">{label}</span>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 focus:border-[#E50914]/50 text-sm outline-none" />
    </label>
  );
}
function Textarea({ label, value, onChange, rows = 3, testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 block mb-1.5">{label}</span>
      <textarea data-testid={testid} rows={rows} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 focus:border-[#E50914]/50 text-sm outline-none resize-vertical" />
    </label>
  );
}
